import { SsoConnection } from '@choco-admin/types';
import { formatDateWithPreset } from '@choco-admin/utils';
import {
  Card,
  CardBody,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import React from 'react';

import { connectionTypeLabel } from '../../utils';

import { EmailDomainsField } from './EmailDomainsField';

export interface ConnectionSummaryProps {
  connection: SsoConnection;
}

const formatDate = (value?: string): string =>
  value ? formatDateWithPreset(value, 'DAY_FIRST_MEDIUM') : '—';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="space-y-1">
    <p className="text-xs uppercase tracking-wide text-default-400">{label}</p>
    <div className="text-sm text-foreground">{children}</div>
  </div>
);

export const ConnectionSummary: React.FC<ConnectionSummaryProps> = ({
  connection,
}) => (
  <Card>
    <CardBody className="space-y-6">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Type">
          <Chip size="sm" variant="flat" color="secondary">
            {connectionTypeLabel(connection.type)}
          </Chip>
        </Field>
        <Field label="Single sign-out">
          {connection.signOutEnabled ? 'Enabled' : 'Disabled'}
        </Field>
        <Field label="Created">{formatDate(connection.createdAt)}</Field>
        <Field label="Last updated">{formatDate(connection.updatedAt)}</Field>
      </div>

      <Field label="Email domains">
        <EmailDomainsField
          connectionId={connection.id}
          emailDomains={connection.emailDomainDetails}
        />
      </Field>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-default-400">
          Attribute mappings
        </p>
        {connection.attributeMappings.length > 0 ? (
          <div className="overflow-x-auto">
            <Table
              aria-label="Attribute mappings"
              removeWrapper
              className="min-w-fit"
              classNames={{
                table: 'border-collapse',
                th: 'bg-default-100 text-default-700 border border-default-200 font-semibold',
                td: 'border border-default-200',
              }}
            >
              <TableHeader>
                <TableColumn>Sent by the identity provider</TableColumn>
                <TableColumn>Stored on the Choco account</TableColumn>
              </TableHeader>
              <TableBody>
                {connection.attributeMappings.map((mapping) => (
                  <TableRow key={mapping.cognitoAttribute}>
                    <TableCell className="whitespace-nowrap font-mono text-xs text-foreground">
                      {mapping.idpAttribute}
                    </TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-xs text-foreground">
                      {mapping.cognitoAttribute}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-default-500">No attributes are mapped.</p>
        )}
      </div>
    </CardBody>
  </Card>
);
